import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { IEmpresa } from "@shared/models/empresa";
import { environment } from "environments/environment";
import { BehaviorSubject, firstValueFrom, map, Observable, of } from "rxjs";

@Injectable({ providedIn: 'root' })
export class EmpresaService {
  datosEmpresa: IEmpresa;
  inProcess = false;
  private _menu: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  private _empresa: BehaviorSubject<IEmpresa> = new BehaviorSubject<IEmpresa>({
    idempresa: 0,
    razon_social: '',
    idcondicioniva: 0,
    logo: null,
    color_primario: '',
    color_secundario: '',
    articulo_insumo: 0,
    articulo_producto: 0,
    articulo_servicio: 0,
    articulo_fabricados: 0,
    cliente_asociado: 0,
    internos: 0,
    favicon: null,
    deposito_default: null,
    asignacion_entrega: null,
    cuit: null,
    email: null,
    direccion: null,
    numero_telefono: null,
    web: null,
    configura_obra: 1,
    descuenta_stock_venta: 0,
    idlocalidad: null,
    permite_entrega_stock_cero: 0,
    punto_venta_default: null,
    dominio_rh: null
  })

  constructor(private _httpClient: HttpClient) {
    this._empresa.next({
      idempresa: 0,
      razon_social: '',
      idcondicioniva: 0,
      logo: null,
      color_primario: '',
      color_secundario: '',
      articulo_insumo: 0,
      articulo_producto: 0,
      articulo_servicio: 0,
      articulo_fabricados: 0,
      cliente_asociado: 0,
      internos: 0,
      favicon: null,
      deposito_default: null,
      asignacion_entrega: null,
      cuit: null,
      email: null,
      direccion: null,
      numero_telefono: null,
      web: null,
      configura_obra: 1,
      descuenta_stock_venta: 0,
      idlocalidad: 0,
      permite_entrega_stock_cero: 0,
      punto_venta_default: null,
      dominio_rh: null
    })
  }

  get empresa$(): Observable<IEmpresa> {
    return this._empresa.asObservable();
  }

  get menu$(): Observable<any[]> {
    return this._menu.asObservable();
  }

  set _empresaData(empresaData) {
    this.datosEmpresa = empresaData;
    const logoPath = this.normalizeUploadsPath(empresaData.logo);
    const faviconPath = this.normalizeUploadsPath(empresaData.favicon);

    const logoUrl = logoPath
      ? environment.empresa.uploads + logoPath
      : null;

    const faviconUrl = faviconPath
      ? environment.empresa.uploads + faviconPath
      : null;

    this._empresa.next({
      idempresa: empresaData.idempresa,
      razon_social: empresaData.razon_social,
      idcondicioniva: empresaData.idcondicioniva,
      logo: logoUrl,
      color_primario: empresaData.color_primario,
      color_secundario: empresaData.color_secundario,
      articulo_insumo: empresaData.articulo_insumo,
      articulo_producto: empresaData.articulo_producto,
      articulo_servicio: empresaData.articulo_servicio,
      articulo_fabricados: empresaData.articulo_fabricados,
      cliente_asociado: empresaData.cliente_asociado,
      internos: empresaData.internos,
      favicon: faviconUrl,
      deposito_default: empresaData.deposito_default,
      asignacion_entrega: empresaData.asignacion_entrega,
      cuit: empresaData.cuit,
      email: empresaData.email,
      direccion: empresaData.direccion,
      numero_telefono: empresaData.numero_telefono,
      web: empresaData.web,
      configura_obra: empresaData.configura_obra,
      descuenta_stock_venta: empresaData.descuenta_stock_venta,
      idlocalidad: empresaData.idlocalidad,
      permite_entrega_stock_cero: empresaData.permite_entrega_stock_cero,
      punto_venta_default: empresaData.punto_venta_default,
      dominio_rh: empresaData.dominio_rh
    });
  }

  get _empresaData() {
    return this.datosEmpresa;
  }

  parsearTiposArticulo(data: IEmpresa): string[] {
    let tiposPermitidos: string[] = [];

    if (data.articulo_insumo === 1) {
      tiposPermitidos.push('insumo');
    }
    if (data.articulo_producto === 1) {
      tiposPermitidos.push('producto');
    }
    if (data.articulo_servicio === 1) {
      tiposPermitidos.push('servicio');
    }

    return tiposPermitidos;
  }

  parsearTipoCliente(data: IEmpresa): string[] {
    let tipoCliente: string[] = [];

    if (data.cliente_asociado === 1) {
      tipoCliente.push('cliente_asociado');
    }

    return tipoCliente;
  }

  tieneClientesAsociados(): Observable<boolean> {
    return this.empresa$.pipe(
      map(empresa => empresa.cliente_asociado === 1)
    );
  }

  parsearDatosEmpresa(empresa: IEmpresa) {
    return {
      idempresa: empresa.idempresa,
      razon_social: empresa.razon_social,
      idcondicioniva: empresa.idcondicioniva,
      logo: empresa.logo,
      color_primario: empresa.color_primario,
      color_secundario: empresa.color_secundario,
      articulo_insumo: empresa.articulo_insumo,
      articulo_producto: empresa.articulo_producto,
      articulo_servicio: empresa.articulo_servicio,
      articulo_fabricados: empresa.articulo_fabricados,
      cliente_asociado: empresa.cliente_asociado,
      internos: empresa.internos,
      favicon: empresa.favicon,
      deposito_default: empresa.deposito_default,
      asignacion_entrega: empresa.asignacion_entrega,
      cuit: empresa.cuit,
      email: empresa.email,
      direccion: empresa.direccion,
      numero_telefono: empresa.numero_telefono,
      web: empresa.web,
      configura_obra: empresa.configura_obra,
      descuenta_stock_venta: empresa.descuenta_stock_venta,
      idlocalidad: empresa.idlocalidad,
      permite_entrega_stock_cero: empresa.permite_entrega_stock_cero,
      punto_venta_default: empresa.punto_venta_default,
      dominio_rh: empresa.dominio_rh
    };
  }

  async getEmpresaX(): Promise<boolean> {
    this.inProcess = true;

    const res = await firstValueFrom(this._httpClient.post<any>(`${environment.empresa.api}`, null));

    this._empresaData = this.parsearDatosEmpresa(res['empresa']);
    if (res.menu) {
      this._menu.next(res.menu);
    }

    return true;
  }

  getEmpresa() {
    if (!this.inProcess) {
      this.getEmpresaX().then(res => {
        this.inProcess = res;
      })
    }
  }

  get(): Observable<IEmpresa> {
    const empresaData = {
      idempresa: 0,
      razon_social: '',
      idcondicioniva: 0,
      logo: null,
      color_primario: '',
      color_secundario: '',
      articulo_insumo: 0,
      articulo_producto: 0,
      articulo_servicio: 0,
      articulo_fabricados: 0,
      cliente_asociado: 0,
      internos: 0,
      favicon: null,
      deposito_default: null,
      asignacion_entrega: null,
      cuit: null,
      email: null,
      direccion: null,
      numero_telefono: null,
      web: null,
      configura_obra: 1,
      descuenta_stock_venta: 0,
      punto_venta_default: null,
      dominio_rh: null
    }

    return of(empresaData);
  }

  private normalizeUploadsPath(path?: string | null): string | null {
    if (!path) return null;

    let clean = path.trim();

    // Asegura que arranque con /
    if (!clean.startsWith('/')) {
      clean = '/' + clean;
    }

    // Si no contiene /uploads o /webuploads, lo agregamos
    if (!clean.includes('/uploads') && !clean.includes('/webuploads')) {
      clean = '/uploads' + clean;
    }

    return clean;
  }
}